import { Geotab } from "@fairfleet/geotab";
import groupBy from "lodash/groupBy";
import { TripRef } from "../../trip/TripRef";
import getTripsWithAnnotations, { TripWithAnnotations } from "./getTripsWithAnnotations";
import { RESULTS_LIMIT_TRIP_ANNOTATION } from "@/constants/resultsLimits";
import { ResultsCapError } from "@/utilities/error/ResultsCapError";

/**
 * Fetches the {@link TripAnnotation}s of every device in the given trips and links them to the
 * trips of the same device. Only the span from the first start to the last stop is fetched;
 * annotations outside the trips would be dropped by the linking anyway.
 *
 * Throws {@link ResultsCapError} on a full page: callers write based on this read, and a partial
 * read would leave annotations behind.
 *
 * @param geotab - The {@link Geotab} client.
 * @param trips - The trips to link annotations to.
 */
export default async function fetchTripsWithAnnotations(
  geotab: Geotab,
  trips: TripRef[],
): Promise<TripWithAnnotations[]> {
  const tripsByDevice = Object.entries(
    groupBy(
      trips.filter(trip => trip.device?.id),
      trip => trip.device.id,
    ),
  );

  const perDevice = await Promise.all(
    tripsByDevice.map(async ([deviceId, deviceTrips]) => {
      const fromDate = new Date(Math.min(...deviceTrips.map(trip => trip.start.getTime())));
      const toDate = new Date(Math.max(...deviceTrips.map(trip => (trip.stop ?? trip.start).getTime())));
      const annotations = await geotab.get(
        "TripAnnotation",
        { deviceSearch: { id: deviceId }, fromDate, toDate },
        RESULTS_LIMIT_TRIP_ANNOTATION,
      );
      if (annotations.length >= RESULTS_LIMIT_TRIP_ANNOTATION) {
        throw new ResultsCapError("TripAnnotation", RESULTS_LIMIT_TRIP_ANNOTATION);
      }
      return [...getTripsWithAnnotations(deviceTrips, annotations)];
    }),
  );

  return perDevice.flat();
}
